import type {
  PmaDerDeclaration,
  PmaDerPart,
  PmaDerStatusData,
} from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type DeclarationRow = PmaDerDeclaration & { pageIds?: string[] };
type PartRow = PmaDerPart & { pageIds?: string[] };

/**
 * Dense grids for PMA/DER Status (DR-3142). Declarations and parts are separate
 * tables because a page is a statement, a control list, or both. Rows carry
 * `pageIds` (plural) after consolidation — same posture as Engine Workscope.
 */
export function presentPmaDerScalarEntries(
  // The letter scalars only, so a non-exceedance read (same letter fields, DR-6373) renders the same way.
  status: Omit<PmaDerStatusData, 'declarations' | 'parts'> | null | undefined,
): ReadonlyArray<readonly [string, string]> {
  if (!status) return [];
  const entries: Array<readonly [string, string]> = [];
  if (status.statementDate) entries.push(['Statement Date', status.statementDate]);
  if (status.coveragePeriodStart) entries.push(['Coverage Start', status.coveragePeriodStart]);
  // A period that starts at the engine's birth prints no start date (DR-5210).
  else if (status.coverageSinceNew === true) entries.push(['Coverage Start', 'Since new']);
  if (status.coveragePeriodEnd) entries.push(['Coverage End', status.coveragePeriodEnd]);
  if (status.issuerName) entries.push(['Issuer', status.issuerName]);
  if (status.issuerRole) entries.push(['Issuer Role', formatIssuerRole(status.issuerRole)]);
  if (status.esns && status.esns.length > 0) entries.push(['ESNs', status.esns.join(', ')]);
  // DR-6009: what a NIS clearance letter's PMA/DER sentence is about.
  if (status.statementSubject)
    entries.push(['Subject', status.statementSubject === 'aircraft' ? 'Aircraft' : 'Engine']);
  return entries;
}

export function PmaDerDeclarationsTable({
  rows,
  pageIdToPosition,
  onInspect,
}: {
  rows: DeclarationRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: DeclarationRow) => void;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Scope</Th>
          <Th>Finding</Th>
          <Th>Quote</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.scope}-${row.finding}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row)}
              className={extractionRowClass}
            >
              {pageIdToPosition && (
                <Td>
                  <PageIdsCell pageIds={pageIds} pageIdToPosition={pageIdToPosition} />
                </Td>
              )}
              <Td>{formatScope(row.scope)}</Td>
              <Td>{formatFinding(row.finding)}</Td>
              <TruncatedTd value={row.quote} maxWidth="max-w-[480px]" />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PmaDerPartsTable({
  rows,
  pageIdToPosition,
  onInspect,
}: {
  rows: PartRow[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: PartRow) => void;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Part Number</Th>
          <Th>Serial</Th>
          <Th>Description</Th>
          <Th>Type</Th>
          <Th>Approval</Th>
          <Th>Authority</Th>
          <Th>Applicability</Th>
          <Th>Date</Th>
          <Th>Qty</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.partNumber ?? 'part'}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row)}
              className={extractionRowClass}
            >
              {pageIdToPosition && (
                <Td>
                  <PageIdsCell pageIds={pageIds} pageIdToPosition={pageIdToPosition} />
                </Td>
              )}
              <Td className="font-mono">{row.partNumber ?? '—'}</Td>
              <Td className="font-mono">{row.serialNumber ?? '—'}</Td>
              <TruncatedTd value={row.description} />
              <Td>{formatDocumentType(row.documentType)}</Td>
              <TruncatedTd
                value={[row.approvalTypeRaw, row.approvalNumber].filter(Boolean).join(' ') || undefined}
              />
              <Td>{row.issuingAuthority ?? '—'}</Td>
              <TruncatedTd value={row.applicability} />
              <Td>{row.date ?? '—'}</Td>
              <Td>{row.quantity ?? '—'}</Td>
              <TruncatedTd value={row.remarks} />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PageIdsCell({
  pageIds,
  pageIdToPosition,
}: {
  pageIds: string[];
  pageIdToPosition: Map<string, number>;
}) {
  if (pageIds.length === 0) return <>{'—'}</>;
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
      {pageIds.map((pageId) => (
        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
      ))}
    </div>
  );
}

function formatIssuerRole(role: PmaDerStatusData['issuerRole']): string {
  if (role === 'operator') return 'Operator';
  if (role === 'camo_on_behalf') return 'CAMO on behalf';
  return '—';
}

function formatScope(scope: PmaDerDeclaration['scope']): string {
  if (scope === 'pma') return 'PMA';
  if (scope === 'der') return 'DER';
  if (scope === 'non_oem_mod') return 'Non-OEM mod';
  return '—';
}

function formatFinding(finding: PmaDerDeclaration['finding']): string {
  if (finding === 'declared_absent') return 'Absent';
  if (finding === 'present') return 'Present';
  if (finding === 'deferred') return 'Deferred';
  if (finding === 'unclear') return 'Unclear';
  return '—';
}

function formatDocumentType(documentType: PmaDerPart['documentType']): string {
  if (documentType === 'pma_part') return 'PMA part';
  if (documentType === 'der_repair') return 'DER repair';
  return '—';
}
